import { Box, InputAdornment, Typography } from '@mui/material';
import useCharger from 'hooks/useCharger';
import { useTranslation } from 'next-i18next';
import type { ChangeEvent, FC } from 'react';
import React, { useEffect, useState } from 'react';
import { isDeviceProCharger } from 'utils/common/deviceTypeUtils';

import ScrollContainerWrapper from 'components/DeviceConfigComponents/ScrollContainerWrapper';
import FilledTextField from 'components/MuiCustom/FilledTextField';

interface SetupGridProps {
  maximumChargingCurrent: number;
  maximumSinglePhaseCurrent: number;
  schedulerStart: number;
  schedulerEnd: number;
  flexibleTariffStart: number;
  flexibleTariffEnd: number;
  afterPowerOutage: number;
  serial: string | undefined;
  setMaximumChargingCurrent: (value: number) => void;
  setMaximumSinglePhaseCurrent: (value: number) => void;
  setSchedulerStart: (value: number) => void;
  setSchedulerEnd: (value: number) => void;
  setFlexibleTariffStart: (value: number) => void;
  setFlexibleTariffEnd: (value: number) => void;
  setAfterPowerOutage: (value: number) => void;
  setInputErrors: (value: boolean) => void;
  handleSubmitGridSetup: (event: React.FormEvent<HTMLFormElement>) => void;
}

const SetupGrid: FC<SetupGridProps> = props => {
  const {
    maximumChargingCurrent,
    maximumSinglePhaseCurrent,
    schedulerStart,
    schedulerEnd,
    flexibleTariffStart,
    flexibleTariffEnd,
    afterPowerOutage,
    serial,
    setMaximumChargingCurrent,
    setMaximumSinglePhaseCurrent,
    setSchedulerStart,
    setSchedulerEnd,
    setFlexibleTariffStart,
    setFlexibleTariffEnd,
    setAfterPowerOutage,
    setInputErrors,
    handleSubmitGridSetup,
  } = props;
  const { t } = useTranslation();

  const devicetType = useCharger(serial, state => state?.typ);

  const isProCharger = isDeviceProCharger(devicetType);

  const [errorMaximumChargingCurrent, setErrorMaximumChargingCurrent] =
    useState<boolean>(false);
  const [errorMaximumSinglePhaseCurrent, setErrorMaximumSinglePhaseCurrent] =
    useState<boolean>(false);

  const handleMaximumChargingCurrent = (
    e: ChangeEvent<HTMLInputElement>,
  ): void => {
    const value = parseFloat(e.target.value);

    if (!value || value < 6 || value > 32) {
      setErrorMaximumChargingCurrent(true);
    } else {
      setErrorMaximumChargingCurrent(false);
    }

    setMaximumChargingCurrent(value);
  };

  const handleMaxGridCurrent = (e: ChangeEvent<HTMLInputElement>): void => {
    const value = parseFloat(e.target.value);

    if (!value || value < 6 || value > 16) {
      setErrorMaximumSinglePhaseCurrent(true);
    } else {
      setErrorMaximumSinglePhaseCurrent(false);
    }

    setMaximumSinglePhaseCurrent(value);
  };

  const inputErrors =
    errorMaximumChargingCurrent || errorMaximumSinglePhaseCurrent;

  useEffect(() => {
    setInputErrors(inputErrors);
  }, [inputErrors, setInputErrors]);

  return (
    <ScrollContainerWrapper height="60vh">
      <Box mb={4}>
        <Typography align="center" variant="h2" sx={{ mb: 2 }}>
          {t('common:pages.settings.subpages.grid.title')}
        </Typography>
        <Typography align="center" variant="body1">
          {t('common:pages.settings.subpages.grid.description')}
        </Typography>
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        overflow="auto"
        flexGrow={0}
        flexShrink={1}
        flexBasis="auto"
        width="100%"
      >
        <form onSubmit={handleSubmitGridSetup}>
          <Box mb={isProCharger ? 4 : 2}>
            <FilledTextField
              label={t(
                'common:pages.settings.subpages.grid.maximum_charging_current',
              )}
              name="maximumChargingCurrent"
              type="number"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography variant="body2" color="grey.600">
                      {t('common:ampere_long')}
                    </Typography>
                  </InputAdornment>
                ),
                inputProps: {
                  min: 6,
                  max: 32,
                },
              }}
              value={maximumChargingCurrent}
              onChange={handleMaximumChargingCurrent}
              error={errorMaximumChargingCurrent}
              helperText={
                errorMaximumChargingCurrent
                  ? t(
                      'common:pages.settings.subpages.grid.maximum_charging_current_error',
                      {
                        max: 32,
                      },
                    )
                  : undefined
              }
              fullWidth
            />
            <Typography variant="body2" pt={1} color="grey.600" pl={1}>
              {t(
                'common:pages.settings.subpages.grid.maximum_charging_current_info',
              )}
            </Typography>
          </Box>
          {isProCharger ? null : (
            <Box mb={4}>
              <FilledTextField
                label={t(
                  'common:pages.settings.subpages.grid.maximum_single_charging_current',
                )}
                name="maximumSinglePhaseCurrent"
                type="number"
                value={maximumSinglePhaseCurrent}
                onChange={handleMaxGridCurrent}
                error={errorMaximumSinglePhaseCurrent}
                fullWidth
                helperText={
                  errorMaximumSinglePhaseCurrent
                    ? t(
                        'common:pages.settings.subpages.grid.maximum_charging_current_error',
                        {
                          max: 16,
                        },
                      )
                    : null
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography variant="body2" color="grey.600">
                        {t('common:ampere_long')}
                      </Typography>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          )}
          <Box mb={2}>
            <Typography mb={1} ml={1.5} variant="h4" display="flex">
              {t('common:pages.settings.subpages.grid.random_maximum_delay')}
            </Typography>
            <FilledTextField
              label={t('common:pages.settings.subpages.grid.scheduler_start')}
              name="schedulerStart"
              type="number"
              value={schedulerStart}
              onChange={e => setSchedulerStart(parseFloat(e.target.value))}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography variant="body2" color="grey.600">
                      {t('common:seconds')}
                    </Typography>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box mb={2}>
            <FilledTextField
              label={t('common:pages.settings.subpages.grid.scheduler_end')}
              name="schedulerEnd"
              type="number"
              value={schedulerEnd}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography variant="body2" color="grey.600">
                      {t('common:seconds')}
                    </Typography>
                  </InputAdornment>
                ),
              }}
              onChange={e => setSchedulerEnd(parseFloat(e.target.value))}
              fullWidth
            />
          </Box>
          <Box mb={2}>
            <FilledTextField
              label={t(
                'common:pages.settings.subpages.grid.flexible_tariff_start',
              )}
              name="flexibleTariffStart"
              type="number"
              value={flexibleTariffStart}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography variant="body2" color="grey.600">
                      {t('common:seconds')}
                    </Typography>
                  </InputAdornment>
                ),
              }}
              onChange={e => setFlexibleTariffStart(parseFloat(e.target.value))}
              fullWidth
            />
          </Box>
          <Box mb={2}>
            <FilledTextField
              label={t(
                'common:pages.settings.subpages.grid.flexible_tariff_end',
              )}
              name="flexibleTariffEnd"
              type="number"
              value={flexibleTariffEnd}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography variant="body2" color="grey.600">
                      {t('common:seconds')}
                    </Typography>
                  </InputAdornment>
                ),
              }}
              onChange={e => setFlexibleTariffEnd(parseFloat(e.target.value))}
              fullWidth
            />
          </Box>
          <Box mb={2}>
            <FilledTextField
              label={t(
                'common:pages.settings.subpages.grid.after_power_outage',
              )}
              name="afterPowerOutage"
              type="number"
              value={afterPowerOutage}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography variant="body2" color="grey.600">
                      {t('common:seconds')}
                    </Typography>
                  </InputAdornment>
                ),
              }}
              onChange={e => setAfterPowerOutage(parseFloat(e.target.value))}
              fullWidth
            />
          </Box>
        </form>
      </Box>
    </ScrollContainerWrapper>
  );
};

export default SetupGrid;
